// The chat a workspace is worked in, in its dock's Chat tab (WorkspaceDock):
// which of the app's one chat console's chats that is (useWorkspaceChat), a
// new one started there (NewChatButton), and the new chat a new workspace
// starts on (requestFreshChat). The console docks in the dock while the
// workspace is up, told what is on the board and what is selected there
// (lib/traceCanvasStore.js). A workspace opened from a chat's investigation
// opens that chat, found by its graph key; a trace's own workspace opens the
// chat last begun in it, or an empty one (a new one when none is empty),
// which it keeps once asked there.
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import HoverTooltip from "./HoverTooltip.jsx";
import { getDockChatBar, subscribeDock } from "../lib/dockStore.js";
import { setTraceCanvasChat, traceCanvasSnapshot } from "../lib/traceCanvasStore.js";

// A new workspace starts on a new chat. Starting one asks for it; the empty
// board the workspace is started on puts the docked console on its empty chat
// (or a new one) once the console has docked there. Only when asked: an empty
// board come back to (a reload, a workspace put away) keeps the chat in front.
let freshWanted = false;
const freshListeners = new Set();
export function requestFreshChat() {
  freshWanted = true;
  for (const f of freshListeners) f();
}
export function useFreshChatOnRequest() {
  const chatBar = useSyncExternalStore(subscribeDock, getDockChatBar, () => null);
  const [asked, setAsked] = useState(0);
  useEffect(() => {
    const f = () => setAsked((n) => n + 1);
    freshListeners.add(f);
    return () => { freshListeners.delete(f); };
  }, []);
  useEffect(() => {
    if (!freshWanted || !chatBar?.onNew) return;
    freshWanted = false;
    chatBar.onNew();
  }, [chatBar, asked]);
}

// The chat a workspace is worked in, put in front of the docked console as the
// workspace comes up (in its own dock, or the workspace home's beside its
// panes) in place of whatever chat was in front: a chat's investigation's
// workspace, that chat; any other, the chat last begun in it, or an empty
// one (a new one when none is empty). With no workspace up (`canvasId` null:
// the home's blank board), an empty one, never a chat carried over from
// another page or workspace; `undefined` while a workspace is on its way in,
// when none is chosen. Chosen once each time a workspace (or the blank board)
// comes up: the reader may turn to another chat after.
//
// An empty chat is no workspace's until it is asked: one first asked while
// this workspace is up is its chat from then on (and no other's). One handed
// over empty and asked anywhere else (the console in another page's column)
// is not brought back here.
//
// Nothing is chosen until the console has its chats in (a reload brings them
// back after the dock is up, and lands on a blank one): chosen before, the
// workspace's own chat was not among them, and the landing replaced it.
export function useWorkspaceChat(canvasId) {
  const chatBar = useSyncExternalStore(subscribeDock, getDockChatBar, () => null);
  const ready = !!chatBar && chatBar.hydrated !== false;
  // Its chat, once the docked console has said which chats there are.
  const chosen = useRef(null);
  useEffect(() => {
    if (!ready || canvasId === undefined) return;
    const key = canvasId || BLANK;
    if (chosen.current === key) return;
    chosen.current = key;
    if (canvasId?.startsWith("chat:")) {
      window.dispatchEvent(new CustomEvent("origin-chat-activate", { detail: { graphKey: canvasId.slice("chat:".length) } }));
      return;
    }
    const tabs = chatBar.tabs || [];
    const own = canvasId ? traceCanvasSnapshot(canvasId).chatId : null;
    const pick = (own && tabs.find((t) => String(t.id) === own))
      || tabs.find((t) => t.active && !t.hasItems)
      || tabs.find((t) => !t.hasItems);
    if (!pick) chatBar.onNew?.();
    else if (!pick.active) pick.onSelect?.();
  }, [chatBar, canvasId, ready]);
  // The chat in front as last seen here, and whether it was empty then.
  const seen = useRef(null);
  useEffect(() => {
    if (!ready) { seen.current = null; return; }
    if (!canvasId || canvasId.startsWith("chat:")) return;
    const active = (chatBar.tabs || []).find((t) => t.active);
    const was = seen.current;
    seen.current = active ? { canvasId, id: String(active.id), empty: !active.hasItems } : null;
    if (active?.hasItems && was?.empty && was.canvasId === canvasId && was.id === String(active.id)) {
      setTraceCanvasChat(canvasId, String(active.id));
    }
  }, [chatBar, canvasId, ready]);
}

const BLANK = "blank";

// A new chat in a workspace's dock, where the Chat tab has no tabs of its own
// to start one from (the chat column's +): the console's own, through what it
// publishes. Asked while a workspace is up, it is that workspace's chat from
// then on (useWorkspaceChat).
const PLUS = <svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" aria-hidden="true"><path d="M6 2.5v7M2.5 6h7" /></svg>;
export function NewChatButton({ className = "", placement = "top" }) {
  const chatBar = useSyncExternalStore(subscribeDock, getDockChatBar, () => null);
  if (!chatBar?.onNew) return null;
  return (
    <HoverTooltip placement={placement} label="New chat">
      <button type="button" aria-label="New chat" data-dock-new-chat="" onClick={() => chatBar.onNew()} className={className}>
        {PLUS}
      </button>
    </HoverTooltip>
  );
}
